import { useAtom, useAction } from '@reatom/npm-react'
import { useState } from 'react'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import { Data } from '@blade/std-lib'
import { Button, CircularLoader } from '@blade/ui-kit'
import { useTranslation, type TransKey } from '@root/shared/localization'
import { Dialog } from '@root/shared/ui-kit'

import {
  updateStateAtom,
  downloadUpdateAction,
  downloadStateAtom,
  installAndRelaunchUpdateAction,
} from '../../atoms'

import { UpdateAnnouncementDialog } from './update-announcement'

import type { UpdateState } from '../../model'
import type { ClassNameProps } from '@root/shared/react'
import type { DialogControlProps } from '@root/shared/ui-kit'

export type UpdateDialogProps = {} & ClassNameProps & DialogControlProps

const updateDialogsStyles = {
  position: 'absolute',
  left: '6px',
  bottom: 'calc(100% + 6px)',
} as const

export const UpdateDialog = (props: UpdateDialogProps) => {
  const { t } = useTranslation()
  const [updateState] = useAtom(updateStateAtom)

  return (
    <Dialog.Root open={props.open} onOpenChange={props.onOpenChange}>
      <Body
        style={updateDialogsStyles}
        describedBy={t(updateStateToTitle(updateState))}
      >
        <Dialog.Header title={t(updateStateToTitle(updateState))} />
        <Content>
          <UpdateStateToContent
            updateState={updateState}
            open={props.open}
            onOpenChange={props.onOpenChange}
          />
        </Content>
      </Body>
    </Dialog.Root>
  )
}

const errorTransKey: TransKey = 'common.error'

const updateStateToTitle = (updateState: UpdateState.UpdateState): TransKey =>
  Data.match.I(updateState)({
    LatestUpdate: () => 'update.latest_update_title' as const,
    UpdateCheck: () => 'update.update_checking' as const,
    UpdateCheckError: () => errorTransKey,
    UpdateAvailable: () => 'update.update_available' as const,
    UpdateDownloadError: () => errorTransKey,
    UpdateReady: () => 'update.update_ready' as const,
    UpdateInstallation: () => 'common.installation' as const,
    UpdateInstallError: () => errorTransKey,
    UpdateRelaunchError: () => errorTransKey,
  })

const UpdateStateToContent = (
  props: {
    updateState: UpdateState.UpdateState
  } & DialogControlProps,
) => {
  const { t } = useTranslation()
  const downloadUpdate = useAction(downloadUpdateAction)
  const installAndRelaunchUpdate = useAction(installAndRelaunchUpdateAction)
  const [isOpen, setIsOpen] = useState(false)

  const [downloadState] = useAtom(downloadStateAtom)

  return match(props.updateState)
    .with({ _tag: 'LatestUpdate' }, () => (
      <>
        <ContentText>{t('update.latest_update_message')}</ContentText>
      </>
    ))
    .with({ _tag: 'UpdateCheck' }, () => <CircularLoader size={32} />)
    .with({ _tag: 'UpdateCheckError' }, () => (
      <>
        <ContentText>{t('update.update_check_error_message')}</ContentText>
      </>
    ))
    .with({ _tag: 'UpdateAvailable' }, ({ update }) =>
      downloadState._tag === 'DownloadStateInProgress' ? (
        <ContentText>{t('common.downloading')}</ContentText>
      ) : (
        <>
          <ContentText>{t('update.new_version_message')}</ContentText>
          <ContentFooter>
            <Button
              variant={'solid'}
              color={'primary'}
              radius={'rounded'}
              size="xs"
              onClick={() => {
                props.onOpenChange?.(false)
                downloadUpdate()
              }}
            >
              {t('common.download')}
            </Button>
            <Button
              variant={'soft'}
              radius={'rounded'}
              size="xs"
              onClick={() => {
                setIsOpen(true)
              }}
            >
              {t('update.about')}
            </Button>
            <UpdateAnnouncementDialog
              open={isOpen}
              version={update.version}
              notesMD={update.notes?.toString() ?? ''}
              onOpenChange={setIsOpen}
            />
          </ContentFooter>
        </>
      ),
    )
    .with({ _tag: 'UpdateDownloadError' }, ({ cause }) => (
      <>
        <ContentText>{t('update.download_error_message')}</ContentText>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <ContentText>{(cause as any)?.cause}</ContentText>
        <ContentFooter>
          <Button
            variant={'solid'}
            color={'primary'}
            radius={'rounded'}
            size="xs"
            onClick={() => {
              downloadUpdate()
            }}
          >
            {t('common.retry')}
          </Button>
        </ContentFooter>
      </>
    ))
    .with({ _tag: 'UpdateReady' }, () => (
      <>
        <ContentText>{t('update.update_ready_message')}</ContentText>
        <ContentFooter>
          <Button
            variant={'solid'}
            color={'primary'}
            radius={'rounded'}
            size="xs"
            onClick={() => installAndRelaunchUpdate()}
          >
            {t('common.install')} & {t('common.relaunch')}
          </Button>
        </ContentFooter>
      </>
    ))
    .with({ _tag: 'UpdateInstallation' }, () => <CircularLoader size={32} />)
    .with({ _tag: 'UpdateInstallError' }, ({ cause }) => (
      <>
        <ContentText>{t('update.installation_error_message')}</ContentText>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <ContentText>{(cause as any)?.cause}</ContentText>
        <ContentFooter>
          <Button
            variant={'solid'}
            color={'primary'}
            radius={'rounded'}
            size="xs"
            onClick={() => installAndRelaunchUpdate()}
          >
            {t('common.retry')}
          </Button>
        </ContentFooter>
      </>
    ))
    .with({ _tag: 'UpdateRelaunchError' }, () => (
      <>
        <ContentText>{t('update.relaunch_error_message')}</ContentText>
      </>
    ))
    .exhaustive()
}

const Body = styled(Dialog.PlainBody)`
  z-index: 1;
`

const Content = styled.div`
  padding-block: 12px;
  padding-inline: 12px;
  width: 300px;
`
const ContentText = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-9);
`
const ContentFooter = styled.div`
  margin-top: 6px;
  display: flex;
  gap: 8px;
`
